import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const CALENDLY_URL = 'https://calendly.com/admin-dailygrinddesign/30min';

const portfolioData = {
  'Party Flyers': [
    { src: 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?crop=entropy&cs=srgb&fm=jpg&w=900&q=85', alt: 'Neon Glow launch party flyer' },
    { src: 'https://images.unsplash.com/photo-1429962714451-bb934ecdc4ec?crop=entropy&cs=srgb&fm=jpg&w=900&q=85', alt: 'Summer rooftop event flyer' },
    { src: 'https://images.unsplash.com/photo-1514525253161-7a46d19cd819?crop=entropy&cs=srgb&fm=jpg&w=900&q=85', alt: 'After-hours dance flyer' },
    { src: 'https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?crop=entropy&cs=srgb&fm=jpg&w=900&q=85', alt: 'Block party promotional flyer' },
    { src: 'https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?crop=entropy&cs=srgb&fm=jpg&w=900&q=85', alt: 'Live music night flyer' },
    { src: 'https://images.unsplash.com/photo-1505373877841-8d25f7d46678?crop=entropy&cs=srgb&fm=jpg&w=900&q=85', alt: 'Album release flyer' },
  ],
  Logos: [
    { src: 'https://images.unsplash.com/photo-1545065695-64b67d551e68?crop=entropy&cs=srgb&fm=jpg&w=900&q=85', alt: 'Modern minimal logo concept' },
    { src: 'https://images.unsplash.com/photo-1611162616305-c69b3fa7fbe0?crop=entropy&cs=srgb&fm=jpg&w=900&q=85', alt: 'Brand identity sketch' },
    { src: 'https://images.unsplash.com/photo-1558655146-9f40138edfeb?crop=entropy&cs=srgb&fm=jpg&w=900&q=85', alt: 'Graphic design workspace' },
    { src: 'https://images.unsplash.com/photo-1626785774573-4b799315345d?crop=entropy&cs=srgb&fm=jpg&w=900&q=85', alt: 'Typography exploration' },
    { src: 'https://images.unsplash.com/photo-1533750349088-cd871a92f312?crop=entropy&cs=srgb&fm=jpg&w=900&q=85', alt: 'Logo sketches in notebook' },
    { src: 'https://images.unsplash.com/photo-1542744095-fcf48d80b0fd?crop=entropy&cs=srgb&fm=jpg&w=900&q=85', alt: 'Creative branding studio' },
  ],
  'Web Designs': [
    { src: 'https://images.unsplash.com/photo-1551650975-87deedd944c3?crop=entropy&cs=srgb&fm=jpg&w=900&q=85', alt: 'Mobile app dashboard design' },
    { src: 'https://images.unsplash.com/photo-1467232004584-a241de8bcf5d?crop=entropy&cs=srgb&fm=jpg&w=900&q=85', alt: 'Marketing site landing page' },
    { src: 'https://images.unsplash.com/photo-1559028012-481c04fa702d?crop=entropy&cs=srgb&fm=jpg&w=900&q=85', alt: 'E-commerce homepage layout' },
    { src: 'https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?crop=entropy&cs=srgb&fm=jpg&w=900&q=85', alt: 'SaaS product website' },
    { src: 'https://images.unsplash.com/photo-1593642632559-0c6d3fc62b89?crop=entropy&cs=srgb&fm=jpg&w=900&q=85', alt: 'Restaurant website mockup' },
    { src: 'https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?crop=entropy&cs=srgb&fm=jpg&w=900&q=85', alt: 'Studio portfolio site' },
  ],
};

const PortfolioHero = () => (
  <section className="relative pt-32 md:pt-40 pb-16 bg-gradient-to-br from-red-50 to-white">
    <div className="container mx-auto px-6">
      <div className="max-w-4xl mx-auto text-center">
        <p className="text-red-600 font-semibold uppercase tracking-wider mb-4" data-testid="portfolio-eyebrow">Our Work</p>
        <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
          The work that
          <span className="bg-gradient-to-r from-red-600 to-black bg-clip-text text-transparent"> moves brands forward.</span>
        </h1>
        <p className="text-xl text-gray-600 leading-relaxed">
          A look at recent projects across party flyers, logos, and websites — every piece designed by hand for a real client with a real goal.
        </p>
      </div>
    </div>
  </section>
);

const Thumbnail = ({ item, category, index, onOpen }) => (
  <button
    type="button"
    onClick={() => onOpen(category, index)}
    data-testid={`portfolio-thumb-${category}-${index}`}
    className="group relative overflow-hidden rounded-xl shadow-md hover:shadow-2xl transition-all duration-300 bg-gray-200 aspect-[4/3] focus:outline-none focus:ring-2 focus:ring-red-600"
    aria-label={`Open ${item.alt}`}
  >
    <img src={item.src} alt={item.alt} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-300 flex items-center justify-center">
      <svg className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m-3-3h6" />
      </svg>
    </div>
  </button>
);

const CategorySection = ({ category, items, alt, onOpen }) => (
  <section
    id={`portfolio-${category.toLowerCase().replace(/\s+/g, '-')}`}
    className={`py-16 md:py-20 ${alt ? 'bg-gray-50' : 'bg-white'}`}
  >
    <div className="container mx-auto px-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-baseline justify-between mb-10">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900">{category}</h2>
          <span className="text-gray-500 text-sm">{items.length} projects</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
          {items.map((item, idx) => (
            <Thumbnail key={item.src} item={item} category={category} index={idx} onOpen={onOpen} />
          ))}
        </div>
      </div>
    </div>
  </section>
);

const PortfolioCTA = () => (
  <section className="py-20 bg-gradient-to-r from-red-600 to-black">
    <div className="container mx-auto px-6 text-center">
      <div className="max-w-3xl mx-auto">
        <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">Like what you see?</h2>
        <p className="text-xl text-red-100 mb-8">Tell us about your project and we'll get to work building something just as bold for your brand.</p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/#contact" data-testid="portfolio-cta-contact" className="bg-white text-red-600 px-8 py-4 rounded-xl font-semibold hover:bg-gray-100 transform hover:scale-105 transition-all shadow-lg">
            Start a Project
          </Link>
          <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" data-testid="portfolio-cta-book-call" className="border-2 border-white text-white px-8 py-4 rounded-xl font-semibold hover:bg-white hover:text-red-600 transition-all">
            Book a Call
          </a>
        </div>
      </div>
    </div>
  </section>
);

const Lightbox = ({ state, onClose, onNav }) => {
  useEffect(() => {
    if (!state) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') onNav(1);
      else if (e.key === 'ArrowLeft') onNav(-1);
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [state, onClose, onNav]);

  if (!state) return null;
  const items = portfolioData[state.category];
  const current = items[state.index];

  return (
    <div
      data-testid="portfolio-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={`Viewing ${current.alt}`}
      className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center animate-fade-in"
      onClick={onClose}
    >
      <button type="button" data-testid="lightbox-close" aria-label="Close" onClick={onClose} className="absolute top-4 right-4 md:top-6 md:right-6 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors">
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
      </button>
      <button type="button" data-testid="lightbox-prev" aria-label="Previous" onClick={(e) => { e.stopPropagation(); onNav(-1); }} className="absolute left-2 md:left-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors">
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
      </button>
      <button type="button" data-testid="lightbox-next" aria-label="Next" onClick={(e) => { e.stopPropagation(); onNav(1); }} className="absolute right-2 md:right-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors">
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
      </button>
      <div className="max-w-5xl w-full px-4 md:px-16" onClick={(e) => e.stopPropagation()}>
        <img src={current.src} alt={current.alt} className="w-full max-h-[80vh] object-contain rounded-lg shadow-2xl" />
        <div className="text-center mt-4 text-white">
          <div className="text-sm uppercase tracking-wider text-red-400">{state.category}</div>
          <div className="text-base mt-1 opacity-80">{current.alt}</div>
          <div className="text-xs mt-2 opacity-60">{state.index + 1} / {items.length}</div>
        </div>
      </div>
    </div>
  );
};

const Portfolio = () => {
  const [lightbox, setLightbox] = useState(null);
  const categories = Object.keys(portfolioData);

  const handleOpen = (category, index) => setLightbox({ category, index });
  const handleClose = () => setLightbox(null);
  const handleNav = (dir) => {
    setLightbox((prev) => {
      if (!prev) return prev;
      const len = portfolioData[prev.category].length;
      return { ...prev, index: (prev.index + dir + len) % len };
    });
  };

  return (
    <div className="App">
      <PortfolioHero />
      {categories.map((category, idx) => (
        <CategorySection
          key={category}
          category={category}
          items={portfolioData[category]}
          alt={idx % 2 === 1}
          onOpen={handleOpen}
        />
      ))}
      <PortfolioCTA />
      <Lightbox state={lightbox} onClose={handleClose} onNav={handleNav} />
    </div>
  );
};

export default Portfolio;
